import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Box, CircularProgress, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { CourseOfferingApi, StudentActivitySummary } from "../api/CourseOfferingApi";
import { DialogApi, IDialog } from '../api/DialogApi';
import { DialogPrompt, DialogPromptApi } from '../api/DialogPromptApi';
import { ILesson, LessonApi } from '../api/LessonApi';
import { IUserActivitySummary } from "../api/UserActivityApi";
import { IUser } from "../api/UserApi";
import { UserDialogPromptUserSummary } from '../api/UserDialogPromptApi';
import AppBarContext from '../context/AppBarContext';
import { handleErrorMessage } from './ErrorDialog';
import { getClassNameDescription } from "./dialogPrompt/DialogPromptForm";

interface CourseExerciseActivityProps {
    courseOfferingId: number;
    courseId: number;
}

const createSummary = (): StudentActivitySummary => ({
    course_id: 0,
    lesson_id: 0,
    dialog_id: 0,
    dialog_prompt_id: 0,
    user_id: 0,
    incorrect_answer_count: 0,
    correct_answer_count: 0,
    speaking_attempt_count: 0,
    mean_best_speaking_score: 0,
});

const addToSummary = (
    totalSummary: StudentActivitySummary,
    addedSummary: StudentActivitySummary
) => {
    if (totalSummary.user_id != addedSummary.user_id) {
        throw new Error(`added user_id ${addedSummary.user_id} doesn't match existing user_id ${totalSummary.user_id}`)
    }
    // totalSummary.user_count = addedSummary.user_count < totalSummary.user_count ? addedSummary.user_count : totalSummary.user_count;
    totalSummary.incorrect_answer_count += addedSummary.incorrect_answer_count;
    totalSummary.correct_answer_count += addedSummary.correct_answer_count;

    if (addedSummary.speaking_attempt_count > 0) {
        if (totalSummary.speaking_attempt_count == 0) {
            totalSummary.speaking_attempt_count = addedSummary.speaking_attempt_count;
            totalSummary.mean_best_speaking_score = addedSummary.mean_best_speaking_score;
        } else {
            const totalSummarySpeakingScoreSum = totalSummary.speaking_attempt_count * totalSummary.mean_best_speaking_score
            const addedSummarySpeakingScoreSum = addedSummary.speaking_attempt_count * addedSummary.mean_best_speaking_score
            totalSummary.speaking_attempt_count += addedSummary.speaking_attempt_count;
            totalSummary.mean_best_speaking_score = (totalSummarySpeakingScoreSum + addedSummarySpeakingScoreSum) / totalSummary.speaking_attempt_count
        }
    }
};

function cantorPair(a: number, b: number): number {
    if (!Number.isInteger(a) || !Number.isInteger(b)) {
        throw new Error("cantorPair expects integers");
    }
    if (a < 0 || b < 0) {
        throw new Error("cantorPair expects non-negative integers");
    }

    const s = a + b;
    const z = (s * (s + 1)) / 2 + b;

    if (!Number.isSafeInteger(z)) {
        throw new Error("Result exceeds Number.MAX_SAFE_INTEGER; use BigInt version");
    }
    return z;
}

function cantorUnpair(z: number): [number, number] {
    if (!Number.isInteger(z) || z < 0) {
        throw new Error("cantorUnpair expects a non-negative integer");
    }
    if (!Number.isSafeInteger(z)) {
        throw new Error("z exceeds Number.MAX_SAFE_INTEGER; use BigInt version");
    }

    // s = floor((sqrt(8z+1)-1)/2)
    const s = Math.floor((Math.sqrt(8 * z + 1) - 1) / 2);

    const Ts = (s * (s + 1)) / 2; // triangular number
    const b = z - Ts;
    const a = s - b;

    // sanity checks
    if (!Number.isInteger(a) || !Number.isInteger(b) || a < 0 || b < 0) {
        throw new Error("Internal error: unpaired result is not valid");
    }
    return [a, b];
}

const CourseExerciseActivity: React.FC<CourseExerciseActivityProps> = ({ courseOfferingId, courseId }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const [userRoster, setUserRoster] = React.useState<IUser[]>([]);
    const [summaryRoster, setSummaryRoster] = React.useState<UserDialogPromptUserSummary[][]>([]);
    const [userActivitySecRoster, setUserActivitySecRoster] = React.useState<IUserActivitySummary[]>([]);
    const [userCourseSummaryRoster, setUserCourseSummaryRoster] = React.useState<[number, number][]>([]);

    const [lessonList, setLessonList] = React.useState<ILesson[]>([]);

    const [userSummaryMap, setUserSummaryMap] = React.useState<Record<number, StudentActivitySummary>>({})
    const [lessonSummaryMap, setLessonSummaryMap] = React.useState<Record<number, StudentActivitySummary>>({})
    const [dialogSummaryMap, setDialogSummaryMap] = React.useState<Record<number, StudentActivitySummary>>({})
    const [dialogPromptSummaryMap, setDialogPromptSummaryMap] = React.useState<Record<number, StudentActivitySummary>>({})

    // which lessons/dialogs are expanded
    const [expandedUsers, setExpandedUsers] = React.useState<Set<number>>(new Set());
    const [expandedLessons, setExpandedLessons] = React.useState<Set<number>>(new Set());
    const [expandedDialogs, setExpandedDialogs] = React.useState<Set<number>>(new Set());

    // indent sizes (in theme spacing units)
    const INDENT_LESSON = 4; // px padding-left multiplier for lesson rows
    const INDENT_DIALOG = 8;
    const INDENT_PROMPT = 12;

    // lazy-load caches (children by parent)
    const [dialogListByLesson, setDialogListByLesson] = React.useState<Record<number, IDialog[]>>({});
    const [promptListByDialog, setPromptListByDialog] = React.useState<Record<number, DialogPrompt[]>>({});

    const updateUserDialogPromptSummary = React.useCallback((newDialogPromptSummaryList: StudentActivitySummary[]) => {
        // const newCourseSummary = createSummary();

        const userMap: Record<number, StudentActivitySummary> = {};
        const lessonMap: Record<number, StudentActivitySummary> = {};
        const dialogMap: Record<number, StudentActivitySummary> = {};
        const dialogPromptMap: Record<number, StudentActivitySummary> = {};

        for (const newDialogPromptSummary of newDialogPromptSummaryList) {
            // addToSummary(newCourseSummary, newDialogPromptSummary)
            // by course
            const userCourseId = cantorPair(newDialogPromptSummary.user_id, newDialogPromptSummary.course_id)
            if (!userMap[userCourseId]) {
                userMap[userCourseId] = {
                    ...createSummary(),
                    user_id: newDialogPromptSummary.user_id,
                    course_id: newDialogPromptSummary.course_id,
                };
            }
            addToSummary(userMap[userCourseId], newDialogPromptSummary);

            // by lesson
            const userLessonId = cantorPair(newDialogPromptSummary.user_id, newDialogPromptSummary.lesson_id)
            if (!lessonMap[userLessonId]) {
                lessonMap[userLessonId] = {
                    ...createSummary(),
                    user_id: newDialogPromptSummary.user_id,
                    course_id: newDialogPromptSummary.course_id,
                    lesson_id: newDialogPromptSummary.lesson_id,
                };
            }
            addToSummary(lessonMap[userLessonId], newDialogPromptSummary);

            // by dialog
            const userDialogId = cantorPair(newDialogPromptSummary.user_id, newDialogPromptSummary.dialog_id)
            if (!dialogMap[userDialogId]) {
                dialogMap[userDialogId] = {
                    ...createSummary(),
                    user_id: newDialogPromptSummary.user_id,
                    course_id: newDialogPromptSummary.course_id,
                    lesson_id: newDialogPromptSummary.lesson_id,
                    dialog_id: newDialogPromptSummary.dialog_id,
                };
            }
            addToSummary(dialogMap[userDialogId], newDialogPromptSummary);

            // by dialog prompt
            const user_dialog_prompt_id = cantorPair(newDialogPromptSummary.user_id, newDialogPromptSummary.dialog_prompt_id)
            if (!dialogPromptMap[user_dialog_prompt_id]) {
                dialogPromptMap[user_dialog_prompt_id] = {
                    ...createSummary(),
                    user_id: newDialogPromptSummary.user_id,
                    course_id: newDialogPromptSummary.course_id,
                    lesson_id: newDialogPromptSummary.lesson_id,
                    dialog_id: newDialogPromptSummary.dialog_id,
                    dialog_prompt_id: newDialogPromptSummary.dialog_prompt_id,
                };
            }
            addToSummary(dialogPromptMap[user_dialog_prompt_id], newDialogPromptSummary);
        }
        setUserSummaryMap(userMap)
        setLessonSummaryMap(lessonMap)
        setDialogSummaryMap(dialogMap)
        setDialogPromptSummaryMap(dialogPromptMap)
    }, [setUserSummaryMap, setLessonSummaryMap, setDialogSummaryMap, setDialogPromptSummaryMap]);

    React.useEffect(() => {

        try {
            CourseOfferingApi.studentActivity(courseOfferingId).then((response) => {
                setUserRoster(response.data[0]);
                setSummaryRoster(response.data[1]);
                setUserActivitySecRoster(response.data[2])
                updateUserDialogPromptSummary(response.data[3]);
            });
        } catch (err: any) {
            handleErrorMessage(err, setErrorMessage, history, location);
        }

        // Fetch lessons when course changes
        LessonApi.getAll(courseId)
            .then(response => {
                const visibleList = response.data.filter((lesson) => lesson.is_hidden === false);
                setLessonList(visibleList.sort((a, b) => a.order_index - b.order_index));
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })


    }, [courseOfferingId, courseId, history, location, setErrorMessage, updateUserDialogPromptSummary]);


    React.useEffect(() => {
        const courseSummaryList: [number, number][] = [];
        for (const summaryList of summaryRoster) {
            const courseCount: [number, number] = [0, 0];

            summaryList.forEach((item) => {
                const { course_id, dialog_prompt_count, user_dialog_prompt_count } = item;

                if (course_id == courseId) {
                    courseCount[0] += dialog_prompt_count;
                    courseCount[1] += user_dialog_prompt_count;
                }
            });
            courseSummaryList.push(courseCount)
        }
        setUserCourseSummaryRoster(courseSummaryList)
    }, [summaryRoster, courseId, setUserCourseSummaryRoster])


    const calculateUserDialogPromptIcon = (promptCount: [number, number]) => {
        if (!promptCount) {
            return <div />
        }
        if (promptCount[1] == 0) {
            const tooltipText = `0 of ${promptCount[0]} complete`;

            return (
                <Tooltip title={tooltipText} arrow>
                    <Box sx={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center", width: 32, height: 32 }}>
                        <CloseIcon sx={{ color: "#F25555", fontSize: 32 }} />
                    </Box>
                </Tooltip>
            )
        }

        const percentComplete = Math.round((promptCount[1] / promptCount[0]) * 100);
        const tooltipText = `${promptCount[1]} of ${promptCount[0]} complete`;

        return (
            <Tooltip title={tooltipText} arrow>
                <Box sx={{ position: "relative", display: "inline-flex" }}>
                    <CircularProgress
                        variant="determinate"
                        value={percentComplete}
                        size={32}
                        thickness={10}
                        sx={{
                            color: percentComplete === 100 ? "#04A755" : "#52637D",
                        }}
                    />
                </Box>
            </Tooltip>
        );
    };

    const calculateUserActivityTooltip = (activity: IUserActivitySummary) => {
        if (activity == null) return <div />
        return <div>
            {(activity.activity_sec / 60).toFixed(1)} minutes total<br />
            {activity.lesson_sec > 0 && (<>{(activity.lesson_sec / 60).toFixed(1)} minutes on all courses<br /></>)}
            {activity.course_sec > 0 && (<>{(activity.course_sec / 60).toFixed(1)} minutes on this course<br /></>)}
            {activity.ai_chat_sec > 0 && (<>{(activity.ai_chat_sec / 60).toFixed(1)} minutes on AI Chat<br /></>)}
            {activity.comment_sec > 0 && (<>{(activity.comment_sec / 60).toFixed(1)} minutes on writing comments<br /></>)}
            {activity.custom_sentence_sec > 0 && (<>{(activity.custom_sentence_sec / 60).toFixed(1)} minutes on Pronunciation<br /></>)}
            {activity.home_sec > 0 && (<>{(activity.home_sec / 60).toFixed(1)} minutes on the Home Page<br /></>)}
            {activity.investor_sec > 0 && (<>{(activity.investor_sec / 60).toFixed(1)} minutes on the Investor Page<br /></>)}
            {activity.leaderboard_sec > 0 && (<>{(activity.leaderboard_sec / 60).toFixed(1)} minutes on the Leaderboard<br /></>)}
            {activity.settings_sec > 0 && (<>{(activity.settings_sec / 60).toFixed(1)} minutes on the Settings Page<br /></>)}
        </div>
    }



    const toggleUser = async (userCourseId: number) => {
        setExpandedUsers(prev => {
            const next = new Set(prev);
            if (next.has(userCourseId)) {
                next.delete(userCourseId);
            } else {
                next.add(userCourseId);
            }
            return next;
        });
    };

    const toggleLesson = async (userLessonId: number) => {
        setExpandedLessons(prev => {
            const next = new Set(prev);
            if (next.has(userLessonId)) {
                next.delete(userLessonId);
            } else {
                next.add(userLessonId);
                const [, lessonId] = cantorUnpair(userLessonId)
                // lazy-load dialogs once
                if (!dialogListByLesson[lessonId]) {
                    DialogApi.getAll(lessonId)
                        .then(resp => {
                            const visible = resp.data.filter(d => !d.is_hidden).sort((a, b) => a.order_index - b.order_index);
                            setDialogListByLesson(curr => ({ ...curr, [lessonId]: visible }));
                        })
                        .catch(err => handleErrorMessage(err, setErrorMessage, history, location));
                }
            }
            return next;
        });
    };

    
    const toggleDialog = async (userDialogId: number) => {
        setExpandedDialogs(prev => {
            const next = new Set(prev);
            if (next.has(userDialogId)) {
                next.delete(userDialogId);
            } else {
                next.add(userDialogId);
                const [, dialogId] = cantorUnpair(userDialogId)
                // lazy-load prompts once
                if (!promptListByDialog[dialogId]) {
                    DialogPromptApi.getAll(dialogId)
                        .then(resp => {
                            const visible = resp.data.filter(p => !p.is_hidden).sort((a, b) => a.order_index - b.order_index);
                            setPromptListByDialog(curr => ({ ...curr, [dialogId]: visible }));
                        })
                        .catch(err => handleErrorMessage(err, setErrorMessage, history, location));
                }
            }
            return next;
        });
    };

    // A tiny cell renderer for summary columns
    const SummaryCells: React.FC<{ summary: StudentActivitySummary; emptyCellPadding?: number }> = ({ summary, emptyCellPadding = 0 }) => {
        // const userPercentage = summary.user_count == Number.MAX_SAFE_INTEGER ? '0%' : `${Math.floor(summary.user_count / userRoster.length * 100)}%`
        // const user_count = summary.user_count == Number.MAX_SAFE_INTEGER ? 0 : summary.user_count;
        const incorrectCorrectRatio = summary.correct_answer_count == 0 ? '' : (summary.incorrect_answer_count / summary.correct_answer_count).toFixed(2)
        return (<>
            {Array.from({ length: emptyCellPadding }).map((_, index) => (
                <TableCell key={`empty-${index}`}></TableCell>
            ))}
            <TableCell align="right">{summary.incorrect_answer_count} / {summary.correct_answer_count} <br /> {incorrectCorrectRatio}</TableCell>
            <TableCell align="right">{summary.speaking_attempt_count}</TableCell>
            <TableCell align="right">{summary.mean_best_speaking_score.toFixed(2)}</TableCell>
        </>)
    };

    const DialogPromptDescription: React.FC<{ dialogPrompt: DialogPrompt }> = ({ dialogPrompt }) => {
        let description = dialogPrompt.prompt.substring(0, 30)

        if ('new_word' in dialogPrompt && String(dialogPrompt.new_word).trim().length > 0) {
            description = String(dialogPrompt.new_word)
        } else if ('correct_vocabulary_word' in dialogPrompt) {
            const vocabularyWord = (dialogPrompt as { correct_vocabulary_word: { word: string, audio_sentence: string } }).correct_vocabulary_word;
            if (vocabularyWord.audio_sentence.trim().length > 0) {
                description = vocabularyWord.audio_sentence
            } else {
                description = vocabularyWord.word
            }
        } else if ('vocabulary_word_list' in dialogPrompt) {
            const vocabList = (dialogPrompt as { vocabulary_word_list: { word: string }[] }).vocabulary_word_list;
            description = vocabList[0].word + " / " + vocabList[1].word;
        } else if ('teacher_chinese_prompt' in dialogPrompt && String(dialogPrompt.teacher_chinese_prompt).trim().length > 0) {
            description = String(dialogPrompt.teacher_chinese_prompt)
        } else if ('student_response_prompt' in dialogPrompt && dialogPrompt.student_response_prompt != '<Answer/>') {
            description = String(dialogPrompt.student_response_prompt)
        }
        return (<>
            {description}<br />
            <Box sx={{ color: 'primary.main', opacity: 0.5 }}>
                {getClassNameDescription(dialogPrompt.class_name)}
            </Box>
        </>)
    };


    // return (<>
    //     <Typography variant="h2" sx={{ color: "#002A6E", textAlign: "center", mt: 2 }}>
    //         Student Activity
    //     </Typography>

    //     <TableContainer component={Paper}>
    //         <Table>
    //             <TableHead>
    //                 <TableRow>
    //                     <TableCell>Name</TableCell>
    //                     <TableCell>% Done</TableCell>
    //                     <TableCell>Time</TableCell>
    //                     <TableCell align="right">Incorrect / Correct</TableCell>
    //                     <TableCell align="right">Speaking<br />Attempts</TableCell>
    //                     <TableCell align="right">Mean<br />Best Speaking Score</TableCell>
    //                 </TableRow>
    //             </TableHead>
    //             <TableBody>
    //                 {userRoster.map((user, index) => (
    //                     <TableRow key={index}>
    //                         <TableCell>{user.first_name} {user.last_name}</TableCell>
    //                         <TableCell>{calculateUserDialogPromptIcon(userCourseSummaryRoster[index])}</TableCell>
    //                         <TableCell>
    //                             <Tooltip
    //                                 arrow
    //                                 title={calculateUserActivityTooltip(userActivitySecRoster[index])}
    //                             >
    //                                 <div>
    //                                     {userActivitySecRoster[index] &&
    //                                         (userActivitySecRoster[index].course_sec / 60).toFixed(1)}
    //                                     {" / "}
    //                                     {userActivitySecRoster[index] &&
    //                                         (userActivitySecRoster[index].activity_sec / 60).toFixed(1)}
    //                                     &nbsp;mins<br />
    //                                     {userActivitySecRoster[index] && userActivitySecRoster[index].distinct_day_count} days
    //                                 </div>
    //                             </Tooltip>
    //                         </TableCell>
    //                     </TableRow>
    //                 ))}
    //             </TableBody>
    //         </Table>
    //     </TableContainer>


    // </>);

    return (<>
        <Typography variant="h2" sx={{ color: "#002A6E", textAlign: "center", mt: 2 }}>
            Student Activity
        </Typography>

        <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell width={12} />  {/* expander column */}
                        <TableCell>Name</TableCell>
                        <TableCell>% Done</TableCell>
                        <TableCell>Time</TableCell>
                        <TableCell align="right">Incorrect / Correct</TableCell>
                        <TableCell align="right">Speaking<br />Attempts</TableCell>
                        <TableCell align="right">Mean<br />Best Speaking Score</TableCell>
                        {/* <TableCell align="right">Exercises</TableCell>
                        <TableCell align="right">Incorrect</TableCell>
                        <TableCell align="right">Correct</TableCell>
                        <TableCell align="right">Incorrect<br />Correct<br />Ratio</TableCell>
                        <TableCell align="right">Speaking<br />Attempts</TableCell>
                        <TableCell align="right">Mean<br />Best Score</TableCell> */}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {/* Course row */}
                    {/* <TableRow hover>
                        <TableCell width={12}>
                            <IconButton size="small" onClick={toggleCourse}>
                                {isCourseExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                            </IconButton>
                        </TableCell>
                        <TableCell>Course</TableCell>
                        {courseSummary ? <SummaryCells summary={courseSummary} /> : (
                            <>
                                <TableCell align="right">—</TableCell>
                                <TableCell align="right">—</TableCell>
                                <TableCell align="right">—</TableCell>
                                <TableCell align="right">—</TableCell>
                                <TableCell align="right">—</TableCell>
                                <TableCell align="right">—</TableCell>
                            </>
                        )}
                    </TableRow> */}
                    {userRoster.map((user, index) => {
                        if (!user.id) return <></>
                        const userCourseId = cantorPair(user.id, courseId)
                        const userSummary = userSummaryMap[userCourseId] ? userSummaryMap[userCourseId] : createSummary();
                        const isUserExpanded = expandedUsers.has(userCourseId);
                        return (
                            <React.Fragment key={`user-${user.id}`}>
                                <TableRow key={index}>
                                    <TableCell>
                                        <IconButton size="small" onClick={() => toggleUser(userCourseId)}>
                                            {isUserExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                        </IconButton>
                                    </TableCell>
                                    <TableCell>{user.first_name} {user.last_name}</TableCell>
                                    <TableCell>{calculateUserDialogPromptIcon(userCourseSummaryRoster[index])}</TableCell>
                                    <TableCell>
                                        <Tooltip
                                            arrow
                                            title={calculateUserActivityTooltip(userActivitySecRoster[index])}
                                        >
                                            <div>
                                                {userActivitySecRoster[index] &&
                                                    (userActivitySecRoster[index].course_sec / 60).toFixed(1)}
                                                {" / "}
                                                {userActivitySecRoster[index] &&
                                                    (userActivitySecRoster[index].activity_sec / 60).toFixed(1)} mins
                                            </div>
                                        </Tooltip>
                                        {userActivitySecRoster[index] && userActivitySecRoster[index].distinct_day_count} days
                                    </TableCell>
                                    <SummaryCells summary={userSummary} />
                                </TableRow>

                                {/* Lessons (all in SAME TableBody) */}
                                {isUserExpanded && lessonList.map(lesson => {
                                    if (!user.id) return <></>
                                    const userLessonId = cantorPair(user.id, lesson.id)
                                    const lessonSummary = lessonSummaryMap[userLessonId] ? lessonSummaryMap[userLessonId] : createSummary();
                                    const isLessonExpanded = expandedLessons.has(userLessonId);
                                    const dialogList = dialogListByLesson[lesson.id] ? dialogListByLesson[lesson.id] : [];
                                    console.log(`lesson ${lesson.id} dialogList ${dialogList.length}`)
                                    return (
                                        <React.Fragment key={`lesson-${lesson.id}`}>
                                            <TableRow hover>
                                                <TableCell width={12}>
                                                    <IconButton size="small" onClick={() => toggleLesson(userLessonId)}>
                                                        {isLessonExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell sx={{ pl: INDENT_LESSON }}>{lesson.name}</TableCell>
                                                <SummaryCells summary={lessonSummary} emptyCellPadding={2} />
                                            </TableRow>

                                            {/* Dialogs under this lesson */}
                                            {isLessonExpanded && dialogList.map(dialog => {
                                                if (!user.id) return <></>
                                                const userDialogId = cantorPair(user.id, dialog.id)
                                                const dialogSummary = dialogSummaryMap[userDialogId] ? dialogSummaryMap[userDialogId] : createSummary();
                                                const isDialogExpanded = expandedDialogs.has(userDialogId);
                                                const promptList = promptListByDialog[dialog.id] ? promptListByDialog[dialog.id] : [];
                                                return (
                                                    <React.Fragment key={`dialog-${dialog.id}`}>
                                                        <TableRow hover>
                                                            <TableCell width={12}>
                                                                <IconButton size="small" onClick={() => toggleDialog(userDialogId)}>
                                                                    {isDialogExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                                                </IconButton>
                                                            </TableCell>
                                                            <TableCell sx={{ pl: INDENT_DIALOG }}>
                                                                {dialog.title}<br />
                                                                <Box sx={{ color: 'primary.main', opacity: 0.5 }}>
                                                                    {dialog.subtitle}
                                                                </Box>
                                                            </TableCell>
                                                            <SummaryCells summary={dialogSummary} emptyCellPadding={2} />
                                                        </TableRow>

                                                        {/* Dialog prompts */}
                                                        {isDialogExpanded && promptList.map(prompt => {
                                                            if (!user.id) return <></>
                                                            const userDialogPromptId = cantorPair(user.id, prompt.id)
                                                            const dialogPromptSummary = dialogPromptSummaryMap[userDialogPromptId] ? dialogPromptSummaryMap[userDialogPromptId] : createSummary();
                                                            return (
                                                                <TableRow hover key={`prompt-${prompt.id}`}>
                                                                    {/* leaf rows: keep expander column but empty to maintain alignment */}
                                                                    <TableCell width={12} />
                                                                    <TableCell sx={{ pl: INDENT_PROMPT }}>
                                                                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                                                                            <DialogPromptDescription dialogPrompt={prompt} />
                                                                        </Box>
                                                                    </TableCell>
                                                                    <SummaryCells summary={dialogPromptSummary} emptyCellPadding={2} />
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </React.Fragment>
                                    );
                                })}

                            </React.Fragment>
                        )
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    </>);
};

export default CourseExerciseActivity;
